<template>
  <div class="warning-dialog">
    <div class="scaled-bg" />
    <div class="layer3d">
      <div
        v-for="p in particles"
        :key="p.id"
        :class="['particle', p.className]"
        :style="{ 'animation-delay': p.delay }"></div>
    </div>

    <div :class="['dialog-content', locale]">
      <div class="close-btn" @click="handleClose">
        <SvgIcon name="svg/close_nobg.svg" size="18px" :fill="false" />
      </div>
      <div class="countdown-wrapper">
        <div class="tip-text"></div>

        <div class="countdown-text">
          <span class="txt">
            <span>{{ $t('倒计时') }}：</span>
            <span class="num">{{ warningSeconds }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import SvgIcon from '@/components/SvgIcon.vue'
import { useRedPacketRain } from '@/composables/useRedPacketRain'

const { locale } = useI18n()

const particles = [
  { id: 0, className: 'pos-center-top', delay: '0.1s' },
  { id: 1, className: 'pos-top-left', delay: '0.1s' },
  { id: 2, className: 'pos-top-right', delay: '0.1s' },
  { id: 3, className: 'pos-bottom-left', delay: '0.15s' },
  { id: 4, className: 'pos-bottom-right', delay: '0.15s' },
  { id: 5, className: 'pos-center-bottom', delay: '0.1s' }
]

const { warningSeconds, closeWarningDialog } = useRedPacketRain()

// 关闭弹窗（不影响状态机，倒计时继续，5s 后红包雨仍会开始）
const handleClose = () => {
  closeWarningDialog()
}
</script>

<style scoped lang="scss">
.warning-dialog {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
}

.scaled-bg {
  width: 268px;
  height: 289px;
  position: absolute;
  background-image: url('@/assets/imgs/activity/rain/warning_bg.webp');
  background-size: contain;
  background-repeat: no-repeat;

  &::after {
    content: '';
    position: absolute;
    background-image: url('@/assets/imgs/activity/rain/warning_coins_splash.webp');
    background-size: contain;
    background-repeat: no-repeat;
    width: 259px;
    height: 249px;
    top: 10px;
    left: 12px;
    transform-origin: center;
    animation: splash-pop 0.35s cubic-bezier(0.15, 0.85, 0.2, 1.25) 0.1s both;
  }

  @keyframes splash-pop {
    0% {
      transform: scale(0.2);
      opacity: 0;
    }

    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
}

.layer3d {
  perspective: 600px;
  transform-style: preserve-3d;
  width: 260px;
  height: 265px;

  .particle {
    position: absolute;
    width: 70px;
    height: 50px;
    background-image: url('@/assets/imgs/activity/rain/atlas.webp');
    background-size: 932px 150px;
    background-repeat: no-repeat;

    // transform: translate3d(92px, 145px, 0);
    // transform: translate3d(-50%, -50%, -600px) scale(0);
    opacity: 0;
    // animation: burst-out 0.4s cubic-bezier(0, 0.6, 0.2, 1) forwards;
    animation: burst-out-elastic 0.55s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  .pos-top-left {
    background-position: -340px -3px;
    --bx: 30px;
    --by: 40px;
    --tx: -30px;
    --ty: 0px;
    --tz: 30px;
    --tscale: 0.5;
    width: 154px;
    height: 140px;
  }

  // 顶部
  .pos-center-top {
    background-position: -658px -2px;
    --bx: 70px;
    --by: 70px;
    --tx: 70px;
    --ty: -30px;
    --tz: 40px;
    --tscale: 0.5;
    width: 140px;
    height: 82px;
  }

  .pos-top-right {
    background-position: -806px -2px;
    --bx: 75px;
    --by: 40px;
    --tx: 165px;
    --ty: 10px;
    --tz: 40px;
    --tscale: 0.5;
    width: 126px;
    height: 114px;
  }

  // 左下
  .pos-bottom-left {
    background-position: -173px -3px;
    --bx: 45px;
    --by: 50px;
    --tx: -30px;
    --ty: 90px;

    --tz: 70px;
    --tscale: 0.4;
    width: 160px;
    height: 145px;
  }

  .pos-bottom-right {
    background-position: -2px -2px;
    --bx: 70px;
    --by: 40px;
    --tx: 140px;
    --ty: 60px;
    --tz: 60px;
    --tscale: 0.4;
    width: 162px;
    height: 150px;
  }

  .pos-center-bottom {
    background-position: -498px -2px;
    --bx: 60px;
    --by: 90px;
    --tx: 55px;
    --ty: 138px;
    --tz: 140px;
    --tscale: 0.7;
    width: 155px;
    height: 110px;
  }

  @keyframes burst-out-elastic {
    0% {
      transform: translate3d(var(--bx), var(--by), 0) scale(0);
      opacity: 0;
    }

    10% {
      opacity: 1;
    }

    80% {
      transform: translate3d(var(--tx), var(--ty), var(--tz)) scale(calc(var(--tscale, 1) * 1.01));
    }

    100% {
      transform: translate3d(var(--tx), var(--ty), var(--tz)) scale(var(--tscale, 1));
      opacity: 1;
    }
  }
}

.dialog-content {
  position: absolute;
  width: 260px;
  height: 265px;

  &.zh-CN {
    .tip-text {
      width: 216px;
      height: 50px;
      background-image: url('@/assets/imgs/activity/rain/warning_text_zh-CN.webp');
      margin-bottom: 0px;
    }
  }

  &.vi-VN {
    .tip-text {
      width: 215px;
      height: 115px;
      background-image: url('@/assets/imgs/activity/rain/warning_text_vi-VN.webp');
    }
  }

  &.en-US {
    .countdown-text {
      font-size: 13px;
    }
  }

  &.ms-MY {
    .countdown-text {
      font-size: 13px;
    }
  }
}

.warning-dialog-enter-active .dialog-content,
.warning-dialog-enter-active .scaled-bg {
  transition: transform 0.45s cubic-bezier(0.22, 1.2, 0.36, 1);
  transform-origin: center;
}

.warning-dialog-enter-from .dialog-content,
.warning-dialog-enter-from .scaled-bg {
  transform: scale(0.6);
}

.warning-dialog-leave-active {
  transition: transform 0.2s ease-in;
  transform-origin: center;
  background: transparent;
}

.warning-dialog-leave-from {
  transform: scale(1);
  background: transparent;
}

.warning-dialog-leave-to {
  transform: scale(0.3);
  background: transparent;
}

.close-btn {
  position: absolute;
  transform: translate(-50%, -50%);
  bottom: -85px;
  left: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 247, 194, 0.6);
  border-radius: 50%;
  background-color: rgba(255, 216, 169, 0.15);
  backdrop-filter: blur(4px);
}

.countdown-wrapper {
  transform: translate(-50%, -50%);
  position: absolute;
  top: 45%;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-text {
  font-size: 15px;
  font-weight: 700;
  color: #ffd66b;
  // margin-bottom: 10px;
  mix-blend-mode: screen;
  background-image: url('@/assets/imgs/activity/rain/warning_btn.webp');
  background-size: 132px 47px;
  cursor: pointer;
  width: 132px;
  height: 47px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  margin-top: -5px;

  .txt {
    margin-bottom: 8px;
    text-shadow: 0.3px 0.5px 0 #ffffff;
    color: #c60505;
    display: flex;

    // .prefix {
    // }
    .num {
      position: relative;

      &::after {
        position: absolute;
        content: 'S';
      }
    }
  }
}

.tip-text {
  width: 316px;
  height: 100px;
  background-image: url('@/assets/imgs/activity/rain/warning_text_en-US.webp');
  background-size: contain;
  background-repeat: no-repeat;
  animation: tip-drop-v3 0.7s linear forwards;
  margin-bottom: -6px;

  &.zh-CN {
    width: 216px;
    height: 50px;
    background-image: url('@/assets/imgs/activity/rain/warning_text_zh-CN.webp');
    margin-bottom: 0px;
  }

  &.vi-VN {
    width: 215px;
    height: 115px;
    background-image: url('@/assets/imgs/activity/rain/warning_text_vi-VN.webp');
  }
}

@keyframes tip-drop-v3 {
  /* 加速下落 */
  0% {
    transform: translateY(-90px) scaleY(1.3) scaleX(0.8);
    opacity: 0;
    animation-timing-function: cubic-bezier(0.47, 0, 0.745, 0.715);
  }

  /* 触地准备变形 */
  45% {
    transform: translateY(0) scaleY(1) scaleX(1);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
  }

  50% {
    /* 触地挤压：变矮变宽 */
    transform: translateY(0) scaleY(0.75) scaleX(1.15);
  }

  65% {
    /* 第一次反弹：高度降低，略微拉伸 */
    transform: translateY(-3px) scaleY(1.05) scaleX(0.95);
    animation-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
  }

  80% {
    /* 第二次落地：轻微挤压 */
    transform: translateY(0) scaleY(0.95) scaleX(1.02);
  }

  90% {
    /* 微小抖动：几乎看不见的跳动，增加质感 */
    transform: translateY(-0.5px) scaleY(1);
  }

  100% {
    transform: translateY(0) scale(1);
  }
}
</style>
